import { useQueryClient } from "@tanstack/react-query";
import { TransactionCodesTable } from "features/fund-family/setup/fund/txcodes/TransactionCodesTable";
import {
  getTransactionCodeConfigListQueryKey,
  useTransactionCodeConfigUpdate,
} from "gen/endpoints/transactions/transactions";
import { TransactionCodeConfigUpdateData } from "gen/model/transactionCodeConfigUpdateData";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { FormUnsavedIndicator } from "shared/components/form/FormUnsavedIndicator";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageInstructions } from "shared/components/layout/PageInstructions";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { MaybernTableBoxActions } from "shared/components/table/maybern/MaybernTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const TransactionCodesEditPage: React.FC = () => {
  const { t } = useTranslation(["fundFamily"]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { fundFamilyId } = useRequiredStringParams();

  const form = useForm();

  const { mutateAsync: updateTransactionCodeConfig } =
    useTransactionCodeConfigUpdate();

  const [changedData, setChangedData] = useState<
    TransactionCodeConfigUpdateData[]
  >([]);

  const onDataChanged = (data: TransactionCodeConfigUpdateData) => {
    if (changedData.find((d) => d.id === data.id)) {
      setChangedData(changedData.map((d) => (d.id === data.id ? data : d)));
    } else {
      setChangedData((cur) => [...cur, data]);
    }
  };

  const navigateBack = () => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .setup({})
        .fund({})
        .transactionCodes({}).$,
    );
  };

  const onSubmit = async () => {
    await updateTransactionCodeConfig({
      fundFamilyId,
      data: { configs_to_update: changedData },
    });
  };

  const onSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey: getTransactionCodeConfigListQueryKey(fundFamilyId),
    });
    navigateBack();
  };

  return (
    <PageContent isFullHeight={true}>
      <Form form={form} onSubmit={onSubmit} onSuccess={onSuccess}>
        <PageHeader title="Edit Transaction Codes" />
        <PageBody>
          <PageInstructions>
            {t("fundFamily:setup.transactionCodes.instructions")}
          </PageInstructions>
          <TableBox isFullWidth={true}>
            <TableBoxHeader>
              <TableBoxTitle>
                {t("fundFamily:setup.transactionCodes.tableTitle")}
              </TableBoxTitle>
              <MaybernTableBoxActions showExpand={false} />
            </TableBoxHeader>
            <TableBoxBody>
              <TransactionCodesTable
                fundFamilyId={fundFamilyId}
                isEditable={true}
                onDataChanged={onDataChanged}
              />
            </TableBoxBody>
          </TableBox>
        </PageBody>
        <FormFooter>
          <FormUnsavedIndicator />
          <FormFooterActions>
            <FormCancelButton onClick={navigateBack} />
            <FormSubmitButton>
              {t("fundFamily:setup.transactionCodes.save")}
            </FormSubmitButton>
          </FormFooterActions>
        </FormFooter>
      </Form>
    </PageContent>
  );
};
